// Whether some other surface has asked the shell to open the INBOX, and on whom (ART-159, C-Entry).
//
// The `teamPanel.ts` / `settingsPanel.ts` shape, for the same reason: the inbox is an OVERLAY owned by the
// topbar's envelope, and the doors onto it are everywhere the product names a person — a crew profile, a
// friend's card, a chat's member list. Threading an opener prop from the shell down through all of them
// would put the shell in the middle of surfaces that otherwise know nothing about it.
//
// **It carries a PERSON, not a thread**, and that is the whole design: "message Dave" is the request a
// reader makes, and which conversation that resolves to — an existing history or a fresh draft — is the
// inbox's own question, already answered once in `InboxScreen` (`existingDmByPerson`). Carrying a thread id
// would mean every caller re-deriving that, which is the second-authority defect this repo charges the most
// incidents to.
//
// The shell subscribes and CLEARS it once consumed, which is what keeps it a REQUEST rather than a second
// source of truth for "is the inbox open".
import { create } from "zustand";
import { registerStoreReset } from "./stores.js";

interface MessagePanelState {
  /** The person to open a conversation with, or null for "nothing pending". */
  personId: string | null;
  request: (personId: string) => void;
  consume: () => void;
  reset: () => void;
}

export const useMessagePanel = create<MessagePanelState>((set) => ({
  personId: null,
  request: (personId) => set({ personId }),
  consume: () => set({ personId: null }),
  reset: () => set({ personId: null }),
}));

// Scorch on org/band switch + logout — a pending "message Dave" from the previous tenant must not fire over
// the new one (stores.ts). Messaging is person-scoped and Dave is still Dave, but the SURFACE that asked is
// gone, and a dialog opening by itself after a switch is the scorch's whole job.
registerStoreReset(() => useMessagePanel.getState().reset());

/** Imperative opener for non-component call sites. */
export function openMessageWith(personId: string): void {
  useMessagePanel.getState().request(personId);
}
